import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/pos-api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const choiceKey = (productId, unitName) => `${productId}::${String(unitName).trim().toLocaleLowerCase()}`;
const money = value => value == null || value === "" ? "—" : new Intl.NumberFormat("id-ID").format(Number(value) || 0);

export default function BranchUnitSyncPanel({ placement = "master" }) {
  const [stores, setStores] = useState([]);
  const [branchContext, setBranchContext] = useState(null);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let active = true;
    const endpoint = placement === "branch" ? "/branch-units/context" : "/master/branch-units/stores";
    api.get(endpoint).then(({ data }) => {
      if (!active) return;
      if (placement === "branch") {
        setBranchContext(data);
        if (data.available) setStores([
          { id: data.source_store_id, name: data.source_store_name },
          { id: data.destination_store_id, name: data.destination_store_name },
        ]);
      } else setStores(data);
    })
      .catch(e => { if (active) setError(apiError(e)); });
    return () => { active = false; };
  }, [placement]);

  async function check() {
    setBusy(true); setError(""); setResult(null); setPreview(null); setSelected({});
    try {
      const source = stores.find(store => /\bSM\s*3\b|MAKMUR\s*3/i.test(store.name));
      const destination = stores.find(store => /\bSM\s*6\b|MAKMUR\s*6/i.test(store.name));
      if (!source || !destination) throw new Error("Cabang SM3 atau SM6 belum terdeteksi.");
      const prefix = placement === "branch" ? "/branch-units" : "/master/branch-units";
      const { data } = await api.post(`${prefix}/preview`, {
        source_store_id: source.id, destination_store_id: destination.id,
      });
      setPreview(data);
    } catch (e) { setError(apiError(e)); }
    finally { setBusy(false); }
  }

  const readyUnits = useMemo(() => (preview?.items || []).flatMap(product => product.units
    .filter(unit => unit.status === "ready").map(unit => ({ ...unit, product_id: product.product_id, product_name: product.name }))), [preview]);
  const chosen = readyUnits.filter(unit => selected[choiceKey(unit.product_id, unit.name)]?.checked);
  const invalid = chosen.some(unit => {
    const setting = selected[choiceKey(unit.product_id, unit.name)];
    return setting.price_mode === "custom" && (setting.custom_price === "" || setting.custom_price == null || !Number.isInteger(Number(setting.custom_price)) || Number(setting.custom_price) < 0);
  });

  function updateChoice(unit, patch) {
    const key = choiceKey(unit.product_id, unit.name);
    setSelected(current => ({ ...current, [key]: { price_mode: "custom", custom_price: "", ...current[key], ...patch } }));
  }

  async function apply() {
    if (!preview || !chosen.length || invalid || busy) return;
    setBusy(true); setError("");
    const units = chosen.map(unit => {
      const setting = selected[choiceKey(unit.product_id, unit.name)];
      return { product_id: unit.product_id, unit_name: unit.name, price_mode: setting.price_mode,
        ...(setting.price_mode === "custom" ? { custom_price: Number(setting.custom_price) } : {}) };
    });
    try {
      const prefix = placement === "branch" ? "/branch-units" : "/master/branch-units";
      const { data } = await api.post(`${prefix}/apply`, {
        source_store_id: preview.source_store_id, destination_store_id: preview.destination_store_id,
        fingerprint: preview.fingerprint, units,
      });
      setResult(data); setPreview(null); setSelected({});
    } catch (e) { setError(`${apiError(e)} Periksa ulang perbandingan sebelum mencoba lagi.`); setPreview(null); setSelected({}); }
    finally { setBusy(false); }
  }

  if (placement === "branch" && !branchContext) return null;
  if (placement === "branch" && !branchContext.available) return null;
  if (placement === "branch" && branchContext && !branchContext.can_copy) return <span aria-label="Status satuan SM3 dan SM6" data-testid="branch-unit-source-status" className="text-xs text-ui-muted">Perbandingan dan penyalinan satuan tersedia dari {branchContext.destination_store_name}.</span>;

  const panelContent = <>
    {placement === "branch" ? <DialogHeader>
      <DialogTitle>Perbedaan Satuan &amp; Harga SM3–SM6</DialogTitle>
      <DialogDescription>Tinjau draf perbedaan terlebih dahulu. Hanya satuan baru yang dipilih yang dapat disalin dari SM3 ke SM6.</DialogDescription>
    </DialogHeader> : <div>
      <h2 id="branch-unit-sync-title" className="text-lg font-semibold">Bandingkan Satuan SM3 dan SM6</h2>
      <p className="text-sm text-ui-muted mt-1">Tampilkan produk yang satuannya berbeda. Hanya satuan baru yang Anda centang akan disalin dari SM3 ke SM6.</p>
    </div>}
    <p className="text-sm text-ui-muted">Yang disalin hanya satuan terpilih dan harga jual satuan tersebut. Harga beli/modal, stok, harga satuan lain, transaksi, dan satuan yang sudah ada tidak diubah. Untuk tiap satuan, pilih ikut harga jual SM3 atau atur harga jual sendiri di SM6.</p>
    <Button type="button" data-testid="branch-unit-preview" onClick={check} disabled={busy || stores.length < 2}>{busy ? "Memproses…" : "Periksa Perbedaan Satuan"}</Button>
    {error && <p role="alert" className="bg-danger-soft text-danger-readable p-3 rounded-lg break-words">{error}</p>}
    {result && <div role="status" data-testid="branch-unit-result" className="bg-brand-soft text-brand-readable p-3 rounded-lg"><strong>{result.copied} satuan disalin ke SM6.</strong><p>{result.products} produk diperbarui. Satuan dan harga lainnya tetap.</p></div>}
    {preview && <div data-testid="branch-unit-preview-results" className="space-y-3">
      <p role="status">{preview.total} produk berbeda · {preview.copyable} satuan siap disalin</p>
      {!preview.items.length && <p className="text-sm text-ui-muted">Satuan SM3 dan SM6 sudah sama.</p>}
      <div className="space-y-3">
        {preview.items.map(product => <article key={product.product_id} className="border border-ui-line rounded-lg p-3 space-y-2" data-testid="branch-unit-product">
          <h3 className="font-semibold">{product.name}</h3>
          {product.units.map((unit, index) => {
            const key = choiceKey(product.product_id, unit.name);
            const status = unit.status;
            return <div key={`${key}-${index}`} className="border-t border-ui-line pt-2 text-sm space-y-2" data-testid="branch-unit-row">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                {status === "ready" && <input aria-label={`Pilih ${unit.name} ${product.name}`} type="checkbox" checked={!!selected[key]?.checked} disabled={busy} onChange={e => updateChoice({ ...unit, product_id: product.product_id }, { checked: e.target.checked })} />}
                <strong>{unit.name}</strong>
                <span>{unit.pieces ?? unit.target_pieces ?? "?"} pcs</span>
                <span className="text-ui-muted">SKU: {unit.barcode || unit.target_barcode || "—"}</span>
                {status === "ready" && <span className="text-ui-muted">Harga SM3: Rp {money(unit.source_price)}</span>}
                {status === "conflict" && <span className="text-amber-700">Sudah ada di SM6 dengan isi/barcode berbeda; tidak ditimpa.</span>}
                {status === "barcode_conflict" && <span className="text-danger-readable">Barcode dipakai produk lain; tidak dapat disalin.</span>}
                {status === "target_only" && <span className="text-ui-muted">Hanya ada di SM6; tidak diubah.</span>}
              </div>
              {status === "ready" && selected[key]?.checked && <div className="grid sm:grid-cols-[minmax(12rem,1fr)_minmax(10rem,1fr)] gap-2 pl-6">
                <label className="text-sm">Pilihan harga jual
                  <select aria-label={`Pilihan harga ${unit.name} ${product.name}`} value={selected[key]?.price_mode || "custom"} disabled={busy} onChange={e => updateChoice({ ...unit, product_id: product.product_id }, { price_mode: e.target.value })} className="block w-full border border-ui-line rounded-lg bg-ui-card p-2 mt-1">
                    <option value="custom">Atur harga sendiri di SM6</option><option value="source" disabled={unit.source_price == null || !Number.isFinite(Number(unit.source_price))}>Ikut harga SM3 (Rp {money(unit.source_price)})</option>
                  </select>
                </label>
                {selected[key]?.price_mode !== "source" && <label className="text-sm">Harga jual SM6 (rupiah bulat)
                  <input aria-label={`Harga sendiri ${unit.name} ${product.name}`} type="number" min="0" step="1" inputMode="numeric" value={selected[key]?.custom_price ?? ""} disabled={busy} onChange={e => updateChoice({ ...unit, product_id: product.product_id }, { custom_price: e.target.value })} className="block w-full border border-ui-line rounded-lg bg-ui-card p-2 mt-1" placeholder="Masukkan harga" />
                </label>}
              </div>}
            </div>;
          })}
        </article>)}
      </div>
      {chosen.length > 0 && <div className="space-y-2">
        {invalid && <p role="alert" className="text-danger-readable text-sm">Isi harga khusus untuk setiap satuan yang dipilih.</p>}
        <p className="text-sm">Terpilih {chosen.length} satuan. Harga khusus yang diisi hanya diterapkan ke satuan-satuan ini.</p>
        <Button type="button" data-testid="branch-unit-apply" disabled={busy || invalid} onClick={apply}>{busy ? "Menyimpan…" : `Salin ${chosen.length} Satuan ke SM6`}</Button>
      </div>}
      </div>}
  </>;

  if (placement === "branch") return <>
    <Button type="button" data-testid="branch-unit-open" variant="outline" onClick={() => setOpen(true)} className="shrink-0">Perbedaan Satuan &amp; Harga</Button>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="branch-unit-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto bg-ui-card border-ui-line text-ui-text">
        <div data-testid="branch-unit-sync-panel" className="space-y-4 min-w-0">{panelContent}</div>
      </DialogContent>
    </Dialog>
  </>;

  return <section aria-labelledby="branch-unit-sync-title" data-testid="branch-unit-sync-panel" className="bg-ui-card border border-ui-line rounded-xl p-4 sm:p-5 space-y-4 min-w-0 mb-4">{panelContent}</section>;
}
